import { motion } from "framer-motion";
import React, { useMemo } from "react";

/**
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isOpen - Determines whether the content is visible or hidden.
 * @param {React.ReactNode} props.children - The content to be animated.
 * @param {string|number} [props.marginTop="10px"] - The margin-top value when the content is visible.
 * @param {string|number} [props.marginBottom="-10px"] - The margin-bottom value when the content is hidden.
 * @param {{open: string|number, closed: string|number}} [props.horizontal={open: 0, closed: 0}] - The x translation values for open and closed states.
 * @param {{open: string|number, closed: string|number}} [props.vertical={open: 0, closed: "-10px"}] - The y translation values for open and closed states.
 * @param {string|number} [props.height="auto"] - The height of the content when visible.
 * @param {number} [props.duration=0.3] - The duration of the animation in seconds.
 * @returns {React.ReactElement} The animated component.
 */
export const SlideInOut = ({
  isOpen,
  children,
  marginTop = "10px",
  marginBottom = "-10px",
  horizontal = { open: 0, closed: 0 },
  vertical = { open: 0, closed: "-10px" },
  height = "auto",
  duration = 0.3
}) => {
  const variants = useMemo(() => {
    const openVariant = {
      pointerEvents: "auto",
      opacity: 1,
      y: vertical.open,
      x: horizontal.open,
      height
    };

    if (marginTop !== 0 && marginTop !== "0") {
      openVariant.marginTop = marginTop;
    }

    return {
      open: openVariant,
      closed: {
        pointerEvents: "none",
        opacity: 0,
        y: vertical.closed,
        x: horizontal.closed,
        height: 0,
        marginTop: 0
      }
    };
  }, [horizontal, vertical, height, marginTop]);

  return (
    <motion.div
      animate={isOpen ? "open" : "closed"}
      style={{ marginBottom: isOpen ? 0 : marginBottom }}
      variants={variants}
      transition={{ duration }}
    >
      {children}
    </motion.div>
  );
};
